import { Link2Off } from "lucide-react";
import { useParams } from "react-router-dom";

import Logo from "@/shared/components/Logo";
import PixelAvatar from "@/shared/components/PixelAvatar";
import { ApiError } from "@/shared/api/errors";
import { useSharedChat } from "@/shared/api/threads";
import Markdown from "@/shared/components/markdown/Markdown";

import type { SharedMessage } from "@/shared/types";

/* `/share/:token` — a conversation someone shared, for anyone with the link.
   Outside the sign-in gate and the workspace chrome: the reader may have no
   account. It shows what the user said and what the agent answered, up to
   when the link was made or last updated; the API leaves out the rest. */

/** One turn per answer: a reply the agent wrote in parts reads as one, and a question it asked
    ends its turn, above the person's answer — which draws nothing when they typed nothing (the
    app they connected answered it). Each turn keyed by its first message. */
function turns(messages: SharedMessage[]): { key: string; role: SharedMessage["role"]; content: string }[] {
  const out: { key: string; role: SharedMessage["role"]; content: string }[] = [];
  const agent = (key: string, content: string) => {
    const last = out.at(-1);
    if (last?.role === "assistant") last.content = `${last.content}\n\n${content}`;
    else out.push({ key, role: "assistant", content });
  };
  for (const m of messages) {
    if (m.role === "assistant") agent(m.id, m.content);
    else {
      if (m.asked) agent(`${m.id}:asked`, m.asked);
      if (m.content) out.push({ key: m.id, role: "user", content: m.content });
    }
  }
  return out;
}

const sharedOn = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

export default function SharedChatPage() {
  const { token = "" } = useParams();
  const { data: chat, isPending, error } = useSharedChat(token);

  return (
    <div className="min-h-screen w-full bg-[var(--surface-sunk)] text-[var(--text-primary)] flex flex-col">
      <header className="h-12 shrink-0 flex items-center gap-1.5 px-4 md:px-5">
        <Logo size={15} />
        <span className="text-[13px] font-semibold tracking-[-0.01em]">Definable</span>
      </header>
      <main className="flex-1 min-w-0 flex flex-col mx-2 mb-2 rounded-[14px] bg-[var(--canvas)] ring-1 ring-[var(--hairline)] shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.06)]">
        {isPending ? (
          <div className="flex-1 flex items-center justify-center gap-1" aria-label="Loading">
            <span className="dot-1 w-1.5 h-1.5 rounded-full bg-[var(--text-tertiary)]" />
            <span className="dot-2 w-1.5 h-1.5 rounded-full bg-[var(--text-tertiary)]" />
            <span className="dot-3 w-1.5 h-1.5 rounded-full bg-[var(--text-tertiary)]" />
          </div>
        ) : error || !chat ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 fade-in px-4">
            <div className="w-10 h-10 rounded-[12px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] flex items-center justify-center">
              <Link2Off className="w-[18px] h-[18px] text-[var(--text-tertiary)]" strokeWidth={1.5} />
            </div>
            <div className="text-[14px] font-medium tracking-[-0.005em]">This link doesn't open a chat</div>
            <div className="text-[12px] text-[var(--text-tertiary)] mt-1 max-w-[320px] text-center">
              {error && !(error instanceof ApiError && error.status === 404)
                ? error.message
                : "It was never shared, the owner stopped sharing it, or the chat was deleted."}
            </div>
          </div>
        ) : (
          <div className="max-w-[720px] w-full mx-auto px-4 py-6 md:px-6 md:py-8 space-y-6 min-w-0">
            <div className="pb-5 border-b hairline">
              <h1 className="text-[19px] font-semibold tracking-[-0.01em] break-words">{chat.title || "Shared chat"}</h1>
              <div className="mt-2 flex items-center gap-2 text-[12px] text-[var(--text-tertiary)] tracking-[-0.005em]">
                <PixelAvatar human={false} seed={chat.agent.seed ?? undefined} hue={chat.agent.hue ?? undefined} size={18} ring rounded={5} />
                <span className="text-[var(--text-secondary)]">{chat.agent.name}</span>
                <span>·</span>
                <span>Shared <span className="font-mono tabular-nums">{sharedOn(chat.shared_at)}</span></span>
              </div>
            </div>
            {turns(chat.messages).map((m) =>
              m.role === "user" ? (
                <div key={m.key} className="flex justify-end fade-in-up min-w-0">
                  <div className="max-w-[85%] rounded-[16px] bg-[var(--surface-2)] px-3.5 py-[9px] text-[14px] leading-[1.5] whitespace-pre-wrap break-words min-w-0">
                    {m.content}
                  </div>
                </div>
              ) : (
                <div key={m.key} className="flex gap-3.5 fade-in-up min-w-0">
                  <div className="pt-[3px] shrink-0">
                    <PixelAvatar human={false} seed={chat.agent.seed ?? undefined} hue={chat.agent.hue ?? undefined} size={22} ring rounded={6} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Markdown text={m.content} />
                  </div>
                </div>
              ),
            )}
            {!chat.messages.length && (
              <p className="text-[11.5px] text-[var(--text-quaternary)] italic text-center">Nothing was said in this chat yet.</p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
